import { observer } from "mobx-react-lite";

import { InlineNode } from "@/components/expression/InlineNode";
import { useDimensionsClassNames } from "@/components/expression/utils/layout/NodeDimensions";
import { GroupNodeModel } from "@/models/nodes/GroupNodeModel";
import { twMerge } from "@/utils/twMerge";

import type { ExpressionComponent } from "@/components/expression/types";
import type { InlineNodeModel } from "@/models/nodes/InlineNodeModel";

type MatrixCellProps = {
  node: InlineNodeModel;
  className?: string;
  align: ColumnAlign;
};

type ColumnAlign = "left" | "center" | "right";

const ALIGN_CLASSES = {
  left: "justify-start",
  center: "justify-center",
  right: "justify-end",
};

const MatrixCell = observer(function MatrixCell({ node, className, align }: MatrixCellProps) {
  const { doc } = node;

  const isSelected = doc.matrixSelection.isSelected(node);

  const dimensionsClassNames = useDimensionsClassNames("square");

  return (
    <div
      data-matrix-cell-id={node.id}
      className={twMerge(
        "table-cell p-1 [&:not(:last-child)]:border-r border-b border-dashed border-transparent hover:bg-blue-50 hover:dark:bg-blue-950",
        className,
        isSelected && "bg-selected-bg border-selected-bg border-solid",
      )}
    >
      <InlineNode
        node={node}
        className={twMerge(dimensionsClassNames, "px-0", ALIGN_CLASSES[align])}
      />
    </div>
  );
});

export const MatrixNode: ExpressionComponent<"matrix"> = observer(function MatrixNode({
  node,
  className,
}) {
  const { doc, parent } = node;
  const casesSystemEnabled = parent?.is(GroupNodeModel) && parent.casesSystemEnabled;

  let columnAlign: ColumnAlign[] = node.props.columnalign
    ? (node.props.columnalign.split(" ") as ColumnAlign[])
    : [];

  if (casesSystemEnabled) {
    columnAlign = Array.from({ length: node.cols }).map(() => "left");
  }

  // Guard against empty matrix (no rows)
  if (node.matrix.length === 0 || !node.matrix[0]) {
    return null;
  }

  return (
    <div className={twMerge("flex", className)}>
      <div className="self-center w-0 selection:bg-transparent">&nbsp;</div>
      <div data-matrix-grid className="relative z-10 table border-collapse group/matrix">
        <div className="table-column-group">
          {node.matrix[0].map((_, colIndex) => {
            const renderAugmentedBar = node.augmentedBarIndices.includes(colIndex);
            return (
              <div
                // eslint-disable-next-line react/no-array-index-key
                key={colIndex}
                className={twMerge(
                  "table-column",
                  renderAugmentedBar && "border-r-[1.5px] border-text",
                  renderAugmentedBar &&
                    doc.matrixSelection.isAugmentedBarSelected(node.id, colIndex) &&
                    "border-node-active",
                )}
              />
            );
          })}
        </div>
        {node.matrix.map((row, rowIndex) => (
          // eslint-disable-next-line react/no-array-index-key
          <div key={rowIndex} className="table-row relative">
            {row.map((cell, cellIndex) => (
              <MatrixCell
                key={cell.id}
                node={cell}
                className={twMerge(
                  "group-hover/matrix:border-blue-100",
                  rowIndex === node.matrix.length - 1 && "border-b-0",
                  node.matrix.length === 1 && "py-0",
                )}
                align={columnAlign[cellIndex] || "center"}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
});
